import './index.scss';
import { h, Component, createRef } from 'preact';
import { BaseAnnotation } from '../base_annotation';
import { capitalize, secondsToTimecode } from 'utilities/formatting';
import { PLAYER_CONTROL_EVENT, PLAYER_EVENT, VIEW_PAGE, ANNOTATION_EVENT } from 'constants/events';
import { AT_TIME_MARGIN_SECONDS } from 'constants/annotations'
import { VscChevronRight} from 'react-icons/vsc'
import LockIcon from '../../../img/icons/LockIcon/LockIcon'
import * as is from 'utilities/is'
import CircleCheckMark from '../../../img/icons/CircleCheckMark';
import CircleTimesIcon from '../../../img/icons/CircleTimesIcon';

const annotationMap = {
    quiz: {name: 'Quiz'}
}

const ACTIVE_START_TIME_MARGIN = 1;

export class ActivitiesPanel extends BaseAnnotation {
    static scalable = false

    constructor(...args) {
        super(...args);
        const { initialState = {} } = this.props;
        this.activityPanelRef = createRef();
        this.eventBus.on(PLAYER_EVENT.TIME_UPDATE, (e, t) => this.timeUpdateHandler(e, t));
        this.eventBus.on(ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE, (e) => { this.onQuizSubmitSuccess(e)} )
        this.state = {
            ...this.state,
            activity: '',
            isOpen: false,
            currentAnnotation: null,
            ...initialState,
            hasCurrentInteraction: false,
            localQuizResponses: {}
        };
        this.notifyOpenness(this.state.isOpen);
        this.eventBus.on(VIEW_PAGE.NAV_BACK_TO_QUIZ_LANDING_PAGE, () => {
            this.resetActivity()
        })
    }

    onQuizSubmitSuccess = (e) => {
        this.setState({
            localQuizResponses: {
                ...this.state.localQuizResponses,
                [e?.response?.quizId]: e?.response
            }
        })
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleDOMClickEvt);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleDOMClickEvt);
    }

    timeUpdateHandler = (e, t) => {
        this.attemptActivityActivation(t.statsCollector.currentTime);
    }

    doesResponseDataExist = (descendants) => {
        const questionsWithSubmittedData = descendants.filter(d => d.responseData);
        return !!questionsWithSubmittedData.length
    }

    getAnnotationAtTime = currentTime => {
        const startTimes = Object.keys(this.descendantTimeMap);
        const shouldActivate = time => currentTime > +time && currentTime <= +time + ACTIVE_START_TIME_MARGIN;
        const timeKey = startTimes.find(shouldActivate);
        return this.descendantTimeMap[timeKey];
    }

    get containsActiveElements() {
        return this?.activityPanelRef?.current?.contains(document.activeElement);
    }

    attemptActivityActivation = currentTime => {
        const { hasCurrentInteraction } = this.state;
        if (hasCurrentInteraction || this.containsActiveElements) return;
        const annotation = this.getAnnotationAtTime(currentTime);
        if (!annotation) return;

        this.setState({ activity: capitalize(annotation.type), isOpen: true, currentAnnotation: annotation });
        this.notifyOpenness(true);
    }

    handleDOMClickEvt = e => {
        const hasCurrentInteraction = this?.activityPanelRef?.current?.contains(e.target);
        this.setState({ hasCurrentInteraction })
    }

    isMappedDescendant = annotation => {
        const { type = '' } = annotation;
        return Object.keys(annotationMap).includes(type);
    }

    get collectedDescendantsByType() {
        return this.props.descendants.reduce((acc, annotation) => {
            if (!this.isMappedDescendant(annotation)) return acc;

            const { type = '' } = annotation;
            const combined = acc[type] ? [...acc[type], annotation] : [annotation];
            acc[type] = combined;
            return acc;
        }, {});
    }

    get descendantTimeMap () {
        return this.props.descendants.reduce((timeData, annotation) => {
            if (!this.isMappedDescendant(annotation)) return timeData;
            const { start } = annotation;

            return {
                ...timeData,
                [start]: annotation
            };
        }, {});
    }

    get sectionValues () {
        return Object.keys(this.collectedDescendantsByType);
    }

    selectAnnotation = (start, annotationId, name, annotation) => {
        // We need to add a quarter second to the time here to compensate for the margin of error used to track atStartTime events on annotations. 
        // Since we're setting an exact current time here that corresponds with an annotations start time,
        // any 'atStartTime - pause' action for an annotation will still fire once playback is resumed.
        // Not ideal, but this is a crazy world.  
        this.eventBus.emit(PLAYER_CONTROL_EVENT.CURRENT_TIME, start + AT_TIME_MARGIN_SECONDS);

        if (annotation?.type === "quiz") {
            requestAnimationFrame(() => this.eventBus.emit(PLAYER_CONTROL_EVENT.PAUSE));
        }
        this.setState({activity: name, currentAnnotation: annotation});
    }

    getCorrectnessIcon = (responseData, annotationId) => {
        const { localQuizResponses = {} } = this.state
        const currentLocalQuizResponse = localQuizResponses[annotationId]

        if (responseData === undefined && !currentLocalQuizResponse) {
            return null;
        }

        const isCorrect = responseData?.isCorrect || currentLocalQuizResponse?.passing;

        return (
            <span className='activity-quiz-icon'>
                {isCorrect ? <CircleCheckMark /> : <CircleTimesIcon />}
            </span>
        )
    }

    get showVeil() {
        return this.props.data?.context?.user?.permissions?.showVeil;
    }

    get buttonRowClasses () {
        const disabledClass = this.showVeil ? ' activity-row-button-disabled' : '';
        return `activity-row-button${disabledClass}`;
    }

    get premiumLockIcon() {
        return (
            <div className='activity-row-button-premium'>
                <span>
                    PREMIUM
                </span>
                <LockIcon/>
            </div>
        )
    }

    createRows = annotations => {
         return annotations
             .sort((a, b) => a.start - b.start)
             .map(annotation => {
                const name = annotation.type === 'quiz' ? 'Quiz Question' : capitalize(annotation.type);
                const { start, responseData } = annotation;
                const text = `${secondsToTimecode(start)} ${name}`;
                // Created alternative buttons for a lite (teacher/admin/caregiver) or premium user
                const renderLiteButton = this.premiumLockIcon;
                const renderPremiumButton = <VscChevronRight className='activity-row-button-chevron'/>;

                const handleClick = () => (
                    this.selectAnnotation(start, annotation.id, name, annotation)
                )
                return (
                    <li className="activity-row">
                        <button onClick={handleClick} disabled={this.showVeil} className={this.buttonRowClasses}>
                            {text}
                            {this.getCorrectnessIcon(responseData, annotation.id)}
                            {this.showVeil ? renderLiteButton : renderPremiumButton}
                        </button>
                    </li>
                )
        });
    }

    get sections () {
        return this.sectionValues.map(descendantType => {
            const title = descendantType;
            const activityRows = this.createRows(this.collectedDescendantsByType[descendantType]);
            return (
                <div className="activities-section-container">
                    <h3 className="activities-section-heading">{title}</h3>
                    <ul>{activityRows}</ul>
                </div>
            )
        })
    }

    resetActivity = () => {
        this.setState({activity: '', currentAnnotation: null});
    }

    get panel () {
        const sections = this.sectionValues.length ? this.sections : 'No activities';

        return (
            <div className='panel-sections-container'>{sections}</div>
        )
    }

    onRenderCloseButton = el => {
        el && is.fn(el.focus) && el.focus();
    }

    onRenderBackButton = el => {
        el && is.fn(el.focus) && el.focus();
    }

    onRenderActivitiesTab = el => {
        el && is.fn(el.focus) && el.focus();
    }

    notifyOpenness(isOpen) {
        const { targetContainer, eventBus } = this.props;

        if (targetContainer && is.fn(targetContainer.setAttribute)) {
            const expandedLabel = isOpen ? 'expanded' : 'collapsed';
            targetContainer?.setAttribute('data-activities', expandedLabel);
            this.eventBus.emit(VIEW_PAGE.RESET_ACTIVITY_PANEL_CONTENTS, {open: isOpen})
        }
    }

    get activeAnnotation() {
        const { children = [] } = this.props;
        const { currentAnnotation = {} } = this.state;

        return children.filter(c => c?.props?.id === currentAnnotation?.id)
    }

    render() {
        return (
            <div ref={this.activityPanelRef} className="hy-activities-panel">
                {!this.state.activity ? this.panel : null}
                <div className="active-annotation">
                    {this.activeAnnotation}
                </div>
            </div>
        )
    }
}